// Moving in from Fullbay (or any other system): what each import can fill, and the column names we recognise.
// Headers are matched loosely (case, spaces, # and punctuation ignored), so "Unit #", "unit_number" and "Unit Number" all match.

export type ImportKind = 'customers' | 'units' | 'parts' | 'vendors';
export type ImportField = { key: string; label: string; required?: boolean; names: string[]; hint?: string };

export const IMPORT_KINDS: { kind: ImportKind; label: string; about: string; fields: ImportField[] }[] = [
  {
    kind: 'customers', label: 'Customers', about: 'Companies you work for. Matched by USDOT, then by name — nothing is added twice.',
    fields: [
      { key: 'name', label: 'Company name', required: true, names: ['customer', 'customer name', 'company', 'company name', 'name', 'customer/company', 'account', 'account name', 'business name'] },
      { key: 'usdot', label: 'USDOT #', names: ['usdot', 'dot', 'dot number', 'usdot number', 'us dot', 'dot #'] },
      { key: 'mc', label: 'MC #', names: ['mc', 'mc number', 'mc #', 'docket'] },
      { key: 'contact', label: 'Contact person', names: ['contact', 'contact name', 'primary contact', 'contact person'] },
      { key: 'phone', label: 'Phone', names: ['phone', 'phone number', 'main phone', 'office phone', 'telephone', 'contact phone'] },
      { key: 'email', label: 'Email', names: ['email', 'e-mail', 'email address', 'billing email', 'contact email', 'invoice email'] },
      { key: 'address', label: 'Address (street)', names: ['address', 'street', 'address 1', 'address line 1', 'billing address', 'street address'] },
      { key: 'city', label: 'City', names: ['city', 'billing city'] },
      { key: 'state', label: 'State', names: ['state', 'st', 'province', 'billing state'] },
      { key: 'zip', label: 'ZIP', names: ['zip', 'zip code', 'postal code', 'postal', 'billing zip'] },
      { key: 'terms_days', label: 'Payment terms (days)', names: ['terms', 'payment terms', 'net', 'net days', 'terms days'], hint: '"Net 30" or 30' },
      { key: 'notes', label: 'Notes', names: ['notes', 'note', 'comments', 'memo'] },
    ],
  },
  {
    kind: 'units', label: 'Trucks & trailers', about: 'Units with their owner. Matched by VIN, then by unit # for the same customer. A missing customer is created.',
    fields: [
      { key: 'customer', label: 'Customer (company)', required: true, names: ['customer', 'customer name', 'company', 'owner', 'fleet', 'account', 'customer/company'] },
      { key: 'unit', label: 'Unit #', required: true, names: ['unit', 'unit #', 'unit number', 'unit no', 'truck #', 'truck number', 'trailer #', 'fleet #', 'fleet number', 'number'] },
      { key: 'vin', label: 'VIN', names: ['vin', 'vin #', 'vin number', 'serial', 'serial number'] },
      { key: 'year', label: 'Year', names: ['year', 'model year', 'yr'] },
      { key: 'make', label: 'Make', names: ['make', 'manufacturer', 'brand'] },
      { key: 'model', label: 'Model', names: ['model'] },
      { key: 'type', label: 'Type', names: ['type', 'unit type', 'vehicle type', 'equipment type', 'body type', 'class'], hint: 'Truck, Trailer, Reefer, Day cab…' },
      { key: 'plate', label: 'License plate', names: ['plate', 'license', 'license plate', 'license plate #', 'tag', 'plate #'] },
      { key: 'mileage', label: 'Mileage', names: ['mileage', 'miles', 'odometer', 'current mileage', 'last mileage', 'odometer reading'] },
      { key: 'notes', label: 'Notes', names: ['notes', 'note', 'comments'] },
    ],
  },
  {
    kind: 'parts', label: 'Parts inventory', about: 'Matched by part #. Quantity on hand is set to the number in the file (the change is recorded as a count).',
    fields: [
      { key: 'sku', label: 'Part #', required: true, names: ['part #', 'part number', 'part no', 'part', 'sku', 'item #', 'item number', 'part num', 'number'] },
      { key: 'name', label: 'Description', required: true, names: ['description', 'name', 'part name', 'part description', 'item', 'item name', 'title'] },
      { key: 'stock', label: 'Qty on hand', names: ['qty', 'quantity', 'on hand', 'qty on hand', 'quantity on hand', 'in stock', 'stock', 'qoh', 'available'] },
      { key: 'cost', label: 'Cost', names: ['cost', 'unit cost', 'avg cost', 'average cost', 'last cost', 'purchase price', 'our cost'] },
      { key: 'price', label: 'Sell price', names: ['price', 'sell price', 'sale price', 'retail', 'retail price', 'list price', 'selling price', 'unit price'] },
      { key: 'vendor', label: 'Vendor', names: ['vendor', 'supplier', 'preferred vendor', 'vendor name', 'primary vendor'] },
      { key: 'bin', label: 'Bin / location', names: ['bin', 'location', 'bin location', 'shelf', 'loc'] },
      { key: 'min_stock', label: 'Min qty', names: ['min', 'min qty', 'minimum', 'reorder point', 'min stock', 'reorder level'] },
      { key: 'max_stock', label: 'Max qty', names: ['max', 'max qty', 'maximum', 'max stock'] },
      { key: 'manufacturer', label: 'Manufacturer', names: ['manufacturer', 'mfr', 'mfg', 'brand', 'make'] },
      { key: 'category', label: 'Category', names: ['category', 'class', 'group', 'part category'] },
      { key: 'upc', label: 'UPC / barcode', names: ['upc', 'barcode', 'ean'] },
      { key: 'notes', label: 'Notes', names: ['notes', 'note', 'comments'] },
    ],
  },
  {
    kind: 'vendors', label: 'Vendors', about: 'Parts suppliers. Matched by name.',
    fields: [
      { key: 'name', label: 'Vendor name', required: true, names: ['vendor', 'vendor name', 'name', 'supplier', 'company'] },
      { key: 'contact', label: 'Contact person', names: ['contact', 'contact name', 'rep', 'sales rep'] },
      { key: 'phone', label: 'Phone', names: ['phone', 'phone number', 'telephone'] },
      { key: 'email', label: 'Email', names: ['email', 'e-mail', 'email address'] },
      { key: 'account_no', label: 'Our account #', names: ['account', 'account #', 'account number', 'acct', 'acct #'] },
      { key: 'address', label: 'Address', names: ['address', 'street', 'address 1'] },
      { key: 'terms_days', label: 'Payment terms (days)', names: ['terms', 'payment terms', 'net'] },
      { key: 'notes', label: 'Notes', names: ['notes', 'note', 'comments'] },
    ],
  },
];

export const norm = (s: string) => s.toLowerCase().replace(/[#_.\-/]+/g, ' ').replace(/\s+/g, ' ').trim();

/** Best guess of which file column fills each field (exact name first, then "contains"). */
export function guessMapping(kind: ImportKind, headers: string[]): Record<string, number> {
  const def = IMPORT_KINDS.find(k => k.kind === kind)!;
  const hs = headers.map(h => norm(h));
  const used = new Set<number>();
  const out: Record<string, number> = {};
  for (const pass of ['exact', 'contains'] as const) {
    for (const f of def.fields) {
      if (out[f.key] !== undefined) continue;
      const names = f.names.map(norm);
      const i = hs.findIndex((h, idx) => !used.has(idx) && (pass === 'exact' ? names.includes(h) : names.some(n => n.length > 3 && h.includes(n))));
      if (i >= 0) { out[f.key] = i; used.add(i); }
    }
  }
  return out;
}

/** "$1,234.56" → 123456 cents. Blank or junk → null. */
export function moneyCents(s: string): number | null {
  const t = String(s ?? '').replace(/[$,\s]/g, '');
  if (!t) return null;
  const neg = /^\(.*\)$/.test(t);
  const n = Number(t.replace(/[()]/g, ''));
  return Number.isFinite(n) ? Math.round((neg ? -n : n) * 100) : null;
}

export function wholeNumber(s: string): number | null {
  const t = String(s ?? '').replace(/[,\s]/g, '');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n) : null;
}
